/**
 * ADDITIVE — main_admin platform MCP key for Claude Code.
 * Isolated card. Org keys stay in Settings → MCP Integration.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { Copy, KeyRound, RefreshCw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useApi } from '../../hooks/useApi';

export default function McpPlatformKeyCard() {
  const api = useApi();
  const [status, setStatus] = useState(null);
  const [plain, setPlain] = useState('');
  const [snippet, setSnippet] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/organizations/mcp/platform-key');
      setStatus(res?.data?.data?.mcp || null);
    } catch (_) {
      setStatus(null);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post('/api/v1/organizations/mcp/platform-key');
      const d = res?.data?.data || {};
      setPlain(d.apiKey || '');
      setSnippet(d.clientConfig ? JSON.stringify(d.clientConfig, null, 2) : '');
      setStatus({ hasKey: true, keyPrefix: d.keyPrefix, createdAt: d.createdAt, endpoint: d.endpoint });
      toast.success('Copy the key now — it will not be shown again.');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to generate key');
    } finally {
      setBusy(false);
    }
  };

  const revoke = () => {
    Modal.confirm({
      title: 'Revoke platform MCP key?',
      content: 'Claude Code / Cursor using this key will stop immediately.',
      okText: 'Revoke',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        try {
          await api.delete('/api/v1/organizations/mcp/platform-key');
          setPlain('');
          setSnippet('');
          await load();
          toast.success('Platform key revoked');
        } catch (e) {
          toast.error(e.response?.data?.message || e.message || 'Failed to revoke');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (_) {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-white font-semibold">
            <KeyRound size={16} />
            Platform MCP key
            <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-300">main_admin</span>
          </div>
          <p className="text-gray-400 text-sm mt-0.5">
            Claude Code / Cursor — any org, email tokens, Qdrant. Not an org key.
          </p>
          <p className="text-gray-500 text-xs mt-1">
            {status?.hasKey
              ? `Key ${status.keyPrefix || 'set'}${status.createdAt ? ` · ${new Date(status.createdAt).toLocaleDateString()}` : ''}`
              : 'No platform key yet'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="primary" loading={busy} icon={<RefreshCw size={14} />} onClick={generate}>
            {status?.hasKey ? 'Regenerate' : 'Generate key'}
          </Button>
          {status?.hasKey ? (
            <Button danger disabled={busy} icon={<Trash2 size={14} />} onClick={revoke}>Revoke</Button>
          ) : null}
        </div>
      </div>
      {plain ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs text-emerald-300 bg-black/40 rounded-lg px-3 py-2 break-all">{plain}</code>
            <Button size="small" icon={<Copy size={13} />} onClick={() => copy(plain, 'Key')}>Copy key</Button>
          </div>
          {snippet ? (
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-500">Claude Code mcp.json</span>
                <Button size="small" type="link" onClick={() => copy(snippet, 'Config')}>Copy config</Button>
              </div>
              <pre className="text-[11px] text-gray-300 bg-black/40 rounded-lg px-3 py-2 overflow-x-auto">{snippet}</pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
